export interface ChartImage {
  /** Raw base64 PNG payload (without the `data:` prefix). */
  base64: string;
  /** Display width in pixels for the embedded image. */
  width: number;
  /** Display height in pixels for the embedded image. */
  height: number;
}

export interface CaptureChartV2ImageOptions {
  /** Drawn bold, centered above the chart. */
  title?: string;
  /** Drawn centered below the chart (x-axis label). */
  xAxisLabel?: string;
  /** Drawn rotated along the left edge of the chart (y-axis label). */
  yAxisLabel?: string;
}

const TITLE_FONT_PX = 15;
const AXIS_FONT_PX = 12;
const LABEL_GAP_PX = 6;

const toBase64 = (canvas: HTMLCanvasElement): string =>
  canvas.toDataURL('image/png').split(',')[1] ?? '';

/** Stack every pane canvas vertically (at device resolution) into one canvas. */
const stackCanvases = (
  canvases: HTMLCanvasElement[]
): HTMLCanvasElement | null => {
  const width = Math.max(...canvases.map(canvas => canvas.width));
  const height = canvases.reduce((total, canvas) => total + canvas.height, 0);

  if (width === 0 || height === 0) {
    return null;
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d');

  if (!ctx) {
    return null;
  }

  let offsetY = 0;
  canvases.forEach(source => {
    ctx.drawImage(source, 0, offsetY);
    offsetY += source.height;
  });

  return canvas;
};

/** Read the top-left pixel as a proxy for the chart's background color. */
const sampleBackground = (canvas: HTMLCanvasElement): string => {
  try {
    const ctx = canvas.getContext('2d');
    const data = ctx?.getImageData(0, 0, 1, 1).data;

    if (!data || data[3] === 0) {
      return '#ffffff';
    }

    return `rgb(${data[0]}, ${data[1]}, ${data[2]})`;
  } catch {
    return '#ffffff';
  }
};

const isLightColor = (rgb: string): boolean => {
  const match = rgb.match(/(\d+)[,\s]+(\d+)[,\s]+(\d+)/);

  if (!match) {
    return true;
  }

  const luminance =
    0.299 * Number(match[1]) +
    0.587 * Number(match[2]) +
    0.114 * Number(match[3]);

  return luminance > 140;
};

/** Truncate with an ellipsis so a long label can't overflow the chart width. */
const fitText = (
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number
): string => {
  if (ctx.measureText(text).width <= maxWidth) {
    return text;
  }

  let truncated = text;

  while (
    truncated.length > 1 &&
    ctx.measureText(`${truncated}…`).width > maxWidth
  ) {
    truncated = truncated.slice(0, -1);
  }

  return `${truncated}…`;
};

interface DrawFramedImageParams {
  chartCanvas: HTMLCanvasElement;
  fontFamily: string;
  ratio: number;
  title?: string;
  xAxisLabel?: string;
  yAxisLabel?: string;
}

interface FramedImage {
  canvas: HTMLCanvasElement;
  /** Extra width added by the y-axis label, in CSS pixels. */
  extraWidthCss: number;
  /** Extra height added by the title + x-axis label, in CSS pixels. */
  extraHeightCss: number;
}

/**
 * Composes a new canvas with the chart plus a bold title, an x-axis label
 * below, and a rotated y-axis label on the left. Text is drawn at device
 * resolution so it stays crisp in the embedded Excel image.
 */
const drawFramedImage = ({
  chartCanvas,
  fontFamily,
  ratio,
  title,
  xAxisLabel,
  yAxisLabel,
}: DrawFramedImageParams): FramedImage | null => {
  const topCss = title ? TITLE_FONT_PX + LABEL_GAP_PX * 2 : 0;
  const bottomCss = xAxisLabel ? AXIS_FONT_PX + LABEL_GAP_PX * 2 : 0;
  const rightCss = yAxisLabel ? AXIS_FONT_PX + LABEL_GAP_PX * 2 : 0;

  const top = Math.round(topCss * ratio);
  const bottom = Math.round(bottomCss * ratio);
  const right = Math.round(rightCss * ratio);

  const framed = document.createElement('canvas');
  framed.width = chartCanvas.width + right;
  framed.height = top + chartCanvas.height + bottom;

  const ctx = framed.getContext('2d');

  if (!ctx) {
    return null;
  }

  const background = sampleBackground(chartCanvas);
  const textColor = isLightColor(background) ? '#1a1a1c' : '#f4f4f5';

  ctx.fillStyle = background;
  ctx.fillRect(0, 0, framed.width, framed.height);
  ctx.drawImage(chartCanvas, 0, top);

  ctx.fillStyle = textColor;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  const chartCenterX = chartCanvas.width / 2;

  if (title) {
    ctx.font = `600 ${TITLE_FONT_PX * ratio}px ${fontFamily}`;
    ctx.fillText(fitText(ctx, title, chartCanvas.width), chartCenterX, top / 2);
  }

  if (xAxisLabel) {
    ctx.font = `${AXIS_FONT_PX * ratio}px ${fontFamily}`;
    ctx.fillText(
      fitText(ctx, xAxisLabel, chartCanvas.width),
      chartCenterX,
      top + chartCanvas.height + bottom / 2
    );
  }

  if (yAxisLabel) {
    // y-axis label sits in the right margin, reading top-to-bottom.
    ctx.save();
    ctx.translate(chartCanvas.width + right / 2, top + chartCanvas.height / 2);
    ctx.rotate(Math.PI / 2);
    ctx.font = `${AXIS_FONT_PX * ratio}px ${fontFamily}`;
    ctx.fillText(fitText(ctx, yAxisLabel, chartCanvas.height), 0, 0);
    ctx.restore();
  }

  return {
    canvas: framed,
    extraWidthCss: rightCss,
    extraHeightCss: topCss + bottomCss,
  };
};

/** CSS-pixel display size of the rendered chart (falls back to intrinsic). */
const resolveChartDisplaySize = (
  canvases: HTMLCanvasElement[],
  chartCanvas: HTMLCanvasElement,
  ratio: number
): { width: number; height: number } => ({
  width: Math.round(
    Math.max(...canvases.map(canvas => canvas.getBoundingClientRect().width)) ||
      chartCanvas.width / ratio
  ),
  height: Math.round(
    canvases.reduce(
      (total, canvas) => total + canvas.getBoundingClientRect().height,
      0
    ) || chartCanvas.height / ratio
  ),
});

/**
 * ChartV2 renders each pane on its own `<canvas>`. This stacks every pane's
 * canvas vertically (at device resolution) into a single PNG so the embedded
 * Excel image matches what the user sees, optionally framing it with the
 * chart title and x/y axis labels. Returns `null` when no canvas has mounted.
 */
export const captureChartV2Image = (
  chartNode: HTMLElement | null,
  options: CaptureChartV2ImageOptions = {}
): ChartImage | null => {
  if (!chartNode) {
    return null;
  }

  const canvases = Array.from(chartNode.querySelectorAll('canvas'));

  if (canvases.length === 0) {
    return null;
  }

  const chartCanvas = stackCanvases(canvases);

  if (!chartCanvas) {
    return null;
  }

  const ratio = window.devicePixelRatio || 1;
  const { width: chartDisplayWidth, height: chartDisplayHeight } =
    resolveChartDisplaySize(canvases, chartCanvas, ratio);

  const title = options.title?.trim();
  const xAxisLabel = options.xAxisLabel?.trim();
  const yAxisLabel = options.yAxisLabel?.trim();

  const plainImage: ChartImage = {
    base64: toBase64(chartCanvas),
    width: chartDisplayWidth,
    height: chartDisplayHeight,
  };

  if (!title && !xAxisLabel && !yAxisLabel) {
    return plainImage;
  }

  const fontFamily =
    window.getComputedStyle(chartNode).fontFamily || 'sans-serif';
  const framed = drawFramedImage({
    chartCanvas,
    fontFamily,
    ratio,
    title,
    xAxisLabel,
    yAxisLabel,
  });

  if (!framed) {
    return plainImage;
  }

  return {
    base64: toBase64(framed.canvas),
    width: chartDisplayWidth + framed.extraWidthCss,
    height: chartDisplayHeight + framed.extraHeightCss,
  };
};
